/**
 * Projection sur 30 ans du portefeuille :
 * - Addition des projections des analyses de chaque immeuble acquis
 * - Plus les impacts des projets au moment où ils sont planifiés / terminés
 * - L'appréciation s'applique sur les valeurs ajustées par les projets
 */

import { calculateOverviewKPIs, generateOverviewProjectionData } from '../utils/propertyKpis';
import { calculatePlanningMetrics } from './planningFinance';
import { getProjectDisplayName } from '../utils/planningDisplay';

const PROJECTION_YEARS = 30;

function getProjectCompletionYear(project) {
  const endDateStr = project?.status === 'completed' && project?.actual_end_date
    ? project.actual_end_date
    : project?.planned_end_date;
  if (!endDateStr) return null;
  const endDate = new Date(endDateStr);
  const now = new Date();
  const currentYear = now.getFullYear();
  const endYear = endDate.getFullYear();
  const yearIndex = endYear - currentYear;
  if (yearIndex < 0) return 0;
  if (yearIndex > PROJECTION_YEARS) return null;
  return yearIndex;
}

export function buildProjectionData(properties = [], projects = []) {
  const now = new Date();
  const currentYear = now.getFullYear();

  // Group projects by property
  const projectsByProperty = {};
  (projects || []).forEach((project) => {
    const propertyId = project?.propertyId;
    if (!propertyId) return;
    if (!projectsByProperty[propertyId]) {
      projectsByProperty[propertyId] = [];
    }
    projectsByProperty[propertyId].push(project);
  });

  // Build projections for each property, accounting for projects
  const projectionsByProperty = (properties || []).map((property) => {
    const propertyProjects = projectsByProperty[property.id] || [];

    // Get baseline projection
    const kpis = calculateOverviewKPIs(property);
    const baselineProjection = generateOverviewProjectionData(property, kpis, PROJECTION_YEARS);

    // Calculate project impacts by year
    const projectImpactsByYear = {};
    propertyProjects.forEach((project) => {
      const completionYear = getProjectCompletionYear(project);
      if (completionYear == null) return;

      const useActual = project?.status === 'completed' && project?.actual_cost > 0;
      const metrics = calculatePlanningMetrics(project, property, { useActualCost: useActual });
      const valueCreation = metrics?.valueCreation ?? 0;
      const additionalAnnualIncome = metrics?.additionalAnnualIncome ?? 0;

      if (!projectImpactsByYear[completionYear]) {
        projectImpactsByYear[completionYear] = { valueDelta: 0, incomeDelta: 0, projects: [] };
      }

      projectImpactsByYear[completionYear].valueDelta += valueCreation;
      projectImpactsByYear[completionYear].incomeDelta += additionalAnnualIncome;
      projectImpactsByYear[completionYear].projects.push({
        name: getProjectDisplayName(project, property),
        id: project?.id,
        valueCreation,
        additionalAnnualIncome
      });
    });

    // Rebuild projection with project impacts
    const appreciationRate = (property?.appreciation_rate || 3) / 100;
    const rentGrowth = (property?.rent_growth_rate || 2) / 100;
    const expenseGrowth = 0.02;

    const adjustedProjection = [];
    let cumulativeValueIncrease = 0;
    let cumulativeIncomeIncrease = 0;

    for (let y = 0; y <= PROJECTION_YEARS; y++) {
      const baseline = baselineProjection[y] || {
        propertyValue: 0,
        mortgageBalance: 0,
        equity: 0,
        annualCashflow: 0,
        noi: 0
      };

      // Apply project impacts at completion year
      if (projectImpactsByYear[y]) {
        cumulativeValueIncrease += projectImpactsByYear[y].valueDelta;
        cumulativeIncomeIncrease += projectImpactsByYear[y].incomeDelta;
      }

      // Calculate appreciation on the adjusted value
      // The baseline already has appreciation, but we need to add appreciation on the project value increase
      let additionalAppreciation = 0;
      if (cumulativeValueIncrease > 0 && y > 0) {
        // Find the earliest year with a project impact
        let earliestProjectYear = PROJECTION_YEARS + 1;
        for (let projectYear in projectImpactsByYear) {
          if (parseInt(projectYear) < earliestProjectYear) {
            earliestProjectYear = parseInt(projectYear);
          }
        }

        // Apply appreciation from the project completion year onwards
        if (y >= earliestProjectYear) {
          const yearsOfAppreciation = y - earliestProjectYear;
          additionalAppreciation = cumulativeValueIncrease * (Math.pow(1 + appreciationRate, yearsOfAppreciation) - 1);
        }
      }

      // Calculate income growth on additional income
      let additionalIncomeGrowth = 0;
      if (cumulativeIncomeIncrease > 0 && y > 0) {
        let earliestProjectYear = PROJECTION_YEARS + 1;
        for (let projectYear in projectImpactsByYear) {
          if (parseInt(projectYear) < earliestProjectYear) {
            earliestProjectYear = parseInt(projectYear);
          }
        }

        if (y >= earliestProjectYear) {
          const yearsOfGrowth = y - earliestProjectYear;
          additionalIncomeGrowth = cumulativeIncomeIncrease * (Math.pow(1 + rentGrowth, yearsOfGrowth) - 1);
        }
      }

      const adjustedValue = baseline.propertyValue + cumulativeValueIncrease + additionalAppreciation;
      const adjustedCashflow = baseline.annualCashflow + cumulativeIncomeIncrease + additionalIncomeGrowth;
      const adjustedNOI = baseline.noi + cumulativeIncomeIncrease + additionalIncomeGrowth;

      adjustedProjection.push({
        year: y,
        propertyValue: adjustedValue,
        mortgageBalance: baseline.mortgageBalance,
        equity: adjustedValue - baseline.mortgageBalance,
        annualCashflow: adjustedCashflow,
        noi: adjustedNOI,
        events: projectImpactsByYear[y]?.projects?.map(p => p.name) || []
      });
    }

    return { property, projection: adjustedProjection, projectImpactsByYear };
  });

  // Aggregate all properties
  const chartData = [];
  const projectEvents = [];

  for (let y = 0; y <= PROJECTION_YEARS; y++) {
    let value = 0;
    let mortgage = 0;
    let cashflow = 0;
    let revenue = 0;
    const events = [];

    projectionsByProperty.forEach(({ projection, projectImpactsByYear }) => {
      const point = projection[y];
      if (point) {
        value += point.propertyValue ?? 0;
        mortgage += point.mortgageBalance ?? 0;
        cashflow += point.annualCashflow ?? 0;
        revenue += point.noi ?? 0;
      }

      // Collect project events for this year
      if (projectImpactsByYear[y]) {
        projectImpactsByYear[y].projects.forEach(project => {
          events.push(project.name);
          projectEvents.push({
            year: y,
            yearLabel: currentYear + y,
            projectName: project.name,
            projectId: project.id,
            valueCreation: project.valueCreation,
            additionalAnnualIncome: project.additionalAnnualIncome
          });
        });
      }
    });

    chartData.push({
      year: y,
      yearLabel: currentYear + y,
      value: Math.round(value * 100) / 100,
      equity: Math.round((value - mortgage) * 100) / 100,
      cashflow: Math.round(cashflow * 100) / 100,
      revenue: Math.round(revenue * 100) / 100,
      events
    });
  }

  return {
    chartData,
    projectEvents: projectEvents.sort((a, b) => a.year - b.year)
  };
}

export default { buildProjectionData, PROJECTION_YEARS };
